import { useContext, useEffect, useState, useRef } from "react";
import { haveValue, toast } from "../helper/common";
import TenantContext from "../context/tenant/TenantContext";
import AuthContext from "../context/auth/AuthContext";
import CloseIcon from "../assets/img/closeIcon.svg";
import UserIcon from "../assets/img/user-icon.svg";
import SkorDefaultLoader from "../assets/img/skorDefaultLoader.gif";
import useTranslator from "../hooks/useTranslator";
import { Link } from "react-router-dom";
import ConsentStepModal from "../pages/onboarding/ConsentStepModal";
import copy from "copy-to-clipboard";
import EnvelopeIcon from "../assets/img/envelopeIcon.svg";
import SendIconWhite from "../assets/img/sendIconWhite.svg";
import LoadingSpinner from "./LoadingSpinner";
import moment from "moment";
import "moment/locale/es";

const ShareProfileModal = ({
  isOpen,
  onClose,
  loadLandlords,
  setAllLandlords,
  landlordsOrig,
  setAllLandlordsOrig,
  searchText,
  setSearchText,
  loading1,
  landlords,
  setShowRemove,
  shareProfileFun,
  setRemoveLandlordId,
  processingLandlordId,
}) => {
  const { _t, i18n } = useTranslator();
  const authContext = useContext(AuthContext);
  const { user, loadUserTenant } = authContext;
  const tenantContext = useContext(TenantContext);
  const {
    fullProfileShared,
    loadSharedProfiles,
    checkLandlordExists,
    sendRequestToLandlordToShareProfile,
    getTenantShareProfileRequestEntries,
    cancelTenantShareProfileRequest,
  } = tenantContext;
  const queryParam = new URLSearchParams(window.location.search);
  const [filteredShortlisted, setFilteredShortlisted] = useState([]);
  const [showConsent, setShowConsent] = useState(false);
  const [selectedLandlord, setSelectedLandlord] = useState({});
  const [email, setEmail] = useState("");
  const [emailChecking, setEmailChecking] = useState(false);
  const [emailExists, setEmailExists] = useState(null);
  const [emailLandlordData, setEmailLandlordData] = useState(null);
  const [emailTouched, setEmailTouched] = useState(false);
  const [sendingEmailRequest, setSendingEmailRequest] = useState(false);
  const [pendingShareRequests, setPendingShareRequests] = useState([]);
  const [loadingPendingRequests, setLoadingPendingRequests] = useState(false);
  const [cancelingRequestId, setCancelingRequestId] = useState(null);
  const emailDebounceRef = useRef(null);

  const isValidEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(String(email).toLowerCase());
  };

  useEffect(() => {
    if (!emailTouched) return;

    clearTimeout(emailDebounceRef.current);

    emailDebounceRef.current = setTimeout(async () => {
      if (!email || !isValidEmail(email)) {
        setEmailExists(null);
        return;
      }

      try {
        setEmailChecking(true);
        const res = await checkLandlordExists(email);
        if (res?.exists) {
          setEmailExists(true);
          setEmailLandlordData(res);
        } else {
          setEmailExists(false);
          setEmailLandlordData(null);
        }
      } catch {
        setEmailExists(false);
        setEmailLandlordData(null);
      } finally {
        setEmailChecking(false);
      }
    }, 600);

    return () => clearTimeout(emailDebounceRef.current);
  }, [email, emailTouched]);

  useEffect(() => {
    const userid = localStorage.getItem("saveduserid");
    if (user && user?._id) {
      loadSharedProfiles(user?._id, "full");
    } else if (haveValue(userid)) {
      loadSharedProfiles(userid, "full");
    }
  }, []);

  const loadPendingShareRequests = async () => {
    setLoadingPendingRequests(true);
    try {
      const res = await getTenantShareProfileRequestEntries();
      if (res?.success) {
        setPendingShareRequests(Array.isArray(res?.data) ? res.data : []);
      } else {
        setPendingShareRequests([]);
      }
    } catch (error) {
      console.log(error);
      setPendingShareRequests([]);
    } finally {
      setLoadingPendingRequests(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadPendingShareRequests();
    }
  }, [isOpen]);

  useEffect(() => {
    setFilteredShortlisted(fullProfileShared);
    setAllLandlords((prev) => [...prev]);
    setAllLandlordsOrig((prev) => [...prev]);
  }, [fullProfileShared]);

  const filterlandlord = (searchText) => {
    const temp = [...landlordsOrig];
    const filtered = temp.filter((val) =>
      `${val?.fname_companyName} ${val?.lname}`
        .toLowerCase()
        .includes(searchText.toLowerCase()),
    );
    setAllLandlords(filtered);
  };

  const isPropertyLinked = (landlordid) => {
    for (const profileShared of fullProfileShared) {
      if (
        profileShared.landlordid === landlordid &&
        haveValue(profileShared?.propertyid)
      ) {
        return profileShared;
      }
    }
    return null;
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      const effect_func = async () => {
        await loadLandlords();

        if (haveValue(queryParam.get("filter"))) {
          const name = queryParam.get("filter");
          setSearchText(name);
          filterlandlord(name);
        }
      };
      if ((searchText ?? "").length > 0) {
        effect_func();
      }
    }, 250);

    return () => clearTimeout(handler);
  }, [searchText]);

  const handlecopyUrl = () => {
    copy(
      `${window.location.protocol}//${process.env.REACT_APP_URL_NAME}/profile-request?sharedProfileLink=${user?._id}`,
    );
    toast.success(_t("linkCopied"));
  };

  const getLandlordEmail = (landlordData) =>
    landlordData?.email ||
    landlordData?.landlord_email ||
    landlordData?.landlord_id?.email;

  const shouldShowConsent = (landlordData) => {
    const insuranceRequired =
      landlordData?.insurance_service_status === true &&
      (!haveValue(user?.consents?.gdpr_consent) ||
        !haveValue(user?.consents?.skor_consent));

    const affidavitRequired =
      landlordData?.affidavit_activated === true &&
      !haveValue(user?.consents?.declaration_consent);

    return insuranceRequired || affidavitRequired;
  };

  const sendShareRequest = async (landlordEmail) => {
    if (!landlordEmail) return;

    setSendingEmailRequest(true);
    try {
      const res = await sendRequestToLandlordToShareProfile(
        landlordEmail.trim(),
      );

      if (res?.success) {
        toast.success(res?.message || _t("requestSentSuccessfully"));
        setEmail("");
        setEmailTouched(false);
        setEmailExists(null);
        setEmailLandlordData(null);
        setSelectedLandlord({});
        await loadPendingShareRequests();
        return;
      }

      toast.error(
        res?.key ? _t(res.key) : res?.message || _t("failedToShareProfile"),
      );
    } catch (error) {
      console.log(error);
      toast.error(_t("failedToShareProfile"));
    } finally {
      setSendingEmailRequest(false);
    }
  };

  const handleLandlordRequestFlow = async (landlordData) => {
    const landlordEmail = getLandlordEmail(landlordData);
    if (!landlordEmail) return;

    if (shouldShowConsent(landlordData)) {
      setSelectedLandlord({
        ...landlordData,
        email: landlordEmail,
      });
      setShowConsent(true);
      return;
    }

    await sendShareRequest(landlordEmail);
  };

  const handleSendShareRequest = async () => {
    if (!isValidEmail(email) || emailChecking || emailExists !== true) {
      return;
    }

    await handleLandlordRequestFlow({
      email: email.trim(),
      insurance_service_status: emailLandlordData?.insurance_service_status,
      affidavit_activated: emailLandlordData?.affidavit_activated,
    });
  };

  const getPendingRequestEmail = (request) =>
    request?.landlord_id?.email ||
    request?.landlord_email ||
    request?.email ||
    request?.landlordEmail ||
    "";

  const handleCancelShareRequest = async (requestId) => {
    if (!requestId) return;

    setCancelingRequestId(requestId);
    try {
      const res = await cancelTenantShareProfileRequest(requestId);

      if (res?.success) {
        toast.success(res?.message || _t("requestCancelledSuccessfully"));
        setPendingShareRequests((prev) =>
          prev.filter((request) => request?._id !== requestId),
        );
        return;
      }

      toast.error(
        res?.key ? _t(res.key) : res?.message || _t("failedToRejectRequest"),
      );
    } catch (error) {
      console.log(error);
      toast.error(_t("failedToRejectRequest"));
    } finally {
      setCancelingRequestId(null);
    }
  };

  useEffect(() => {
    const lang = i18n.language?.toLowerCase();

    // Convert "es-ES" → "es"
    const shortLang = lang?.split("-")[0];

    moment.locale(shortLang);
  }, [i18n.language]);

  return (
    isOpen && (
      <div className="wrapper-modal" onClick={onClose}>
        <div
          className={`doc-history-popup total-salary-modal share-profile-modal ${isOpen ? "open" : ""
            }`}
          onClick={(e) => e.stopPropagation()}
        >
          <span className="drawerIcon" onClick={onClose} />
          <div>
            <div className="doc-history-header">
              <div className="d-flex align-items-center custom-gap-2-vw">
                <img
                  src={CloseIcon}
                  alt=""
                  className="doc-history-close-btn"
                  onClick={onClose}
                />
                <h4 className="custom-modal-heading">
                  {_t("shareYourProfile")}
                </h4>
              </div>
            </div>
          </div>
          <div className="listWrapper">
            <div className="tabs-box-pattern">
              <div className="tabs-item d-block">
                <div className="share-profile-options-wrapper">
                  <div className="send-by-email-block">
                    <div className="d-flex align-items-center gap-2-vw mb-2">
                      <img
                        src={EnvelopeIcon}
                        alt="email"
                        className="email-icon"
                      />
                      <h5
                        className="mb-0 fw-bold share-profile-send-email-title"
                        style={{ color: "#253837" }}
                      >
                        {_t("sendByEmail")}
                      </h5>
                    </div>
                    <p className="ps-24px">{_t("sendByEmailDescription")}</p>
                    <div className="w-100 ps-24px">
                      <div className="d-flex align-items-center gap-2-vw">
                        <div className="flex-grow-1 position-relative">
                          <input
                            type="email"
                            className="form-control"
                            placeholder="landlord@email.com"
                            value={email}
                            onChange={(e) => {
                              setEmail(e.target.value);
                              setEmailTouched(true);
                              setEmailExists(null);
                              setEmailLandlordData(null);
                              setEmailChecking(false);
                            }}
                            style={{
                              width: "100%",
                              border:
                                !emailChecking &&
                                  emailTouched &&
                                  emailExists === false &&
                                  isValidEmail(email)
                                  ? ".0694vw solid #fd0000"
                                  : ".0694vw solid #E5E7EB",
                            }}
                          />
                          {emailChecking && (
                            <div
                              style={{
                                position: "absolute",
                                right: "12px",
                                top: "50%",
                                transform: "translateY(-50%)",
                              }}
                            >
                              <LoadingSpinner
                                size={"0.8233vw"}
                                color="#253837"
                                borderWidth={1}
                                className="docs-loading-spinner"
                              />
                            </div>
                          )}
                          {!emailChecking &&
                            emailTouched &&
                            emailExists === true &&
                            isValidEmail(email) && (
                              <img
                                src="/images/checkmark-green.svg"
                                className="url-wrapper-img"
                                style={{
                                  position: "absolute",
                                  right: "12px",
                                  top: "50%",
                                  transform: "translateY(-50%)",
                                  width: "20px",
                                }}
                                alt="status"
                              />
                            )}
                        </div>
                        <button
                          type="button"
                          className="btnNext cursor-pointer d-flex align-items-center justify-content-center gap-2-vw email-send-btn"
                          disabled={
                            !isValidEmail(email) ||
                            emailChecking ||
                            emailExists !== true ||
                            sendingEmailRequest
                          }
                          onClick={handleSendShareRequest}
                          style={{
                            opacity:
                              !isValidEmail(email) ||
                                emailChecking ||
                                emailExists !== true ||
                                sendingEmailRequest
                                ? 0.5
                                : 1,
                          }}
                        >
                          {sendingEmailRequest ? (
                            <LoadingSpinner
                              size={"0.8233vw"}
                              color="#FFFFFF"
                              borderWidth={1}
                              className="docs-loading-spinner"
                            />
                          ) : (
                            <img
                              src={SendIconWhite}
                              alt="send"
                              style={{ width: "16px" }}
                            />
                          )}
                          {sendingEmailRequest ? _t("sending") : _t("send")}
                        </button>
                      </div>
                      {!emailChecking &&
                        emailTouched &&
                        emailExists === false &&
                        isValidEmail(email) && (
                          <span className="error-message mt-1 mb-0 pb-0">
                            {_t("landlordDoesNotExist")}
                          </span>
                        )}
                    </div>
                  </div>

                  <hr
                    className="border-margin"
                    style={{ borderColor: "#ccc" }}
                  />

                  <div className="copy-link-block">
                    <div className="d-flex align-items-center gap-2-vw mb-1-vw">
                      <img
                        src="/images/copy-icon.svg"
                        alt="copy"
                        className="copy-icon"
                      />
                      <h5
                        className="mb-0 fw-bold share-profile-send-email-title"
                        style={{ color: "#1E2528" }}
                      >
                        {_t("copyLink")}
                      </h5>
                    </div>
                    <p className="ps-24px">
                      {_t("copyLinkOr")}{" "}
                      <button
                        type="button"
                        style={{
                          color: "#1E2528",
                          textDecoration: "underline",
                          fontWeight: "500",
                          background: "transparent",
                          border: "none",
                          padding: 0,
                        }}
                        onClick={() => {
                          handlecopyUrl();
                        }}
                      >
                        {_t("copyYourSharingLink")}
                      </button>{" "}
                      {_t("copyLinkAnywhere")}
                    </p>
                  </div>
                </div>

                {!haveValue(searchText) && (
                  <div
                    style={{
                      textAlign: "left",
                    }}
                  >
                    <div className="shareProfileHeader d-flex align-items-center custom-gap-2-vw justify-content-between">
                      <h4 className="sharedWithText mb-0">
                        {haveValue(searchText)
                          ? _t("searchResult")
                          : _t("sharedWith")}
                      </h4>
                      <span className="user-length">
                        {(pendingShareRequests?.length ?? 0) +
                          (filteredShortlisted?.length ?? 0)}{" "}
                        {_t("people")}
                      </span>
                    </div>
                  </div>
                )}

                <div className="tab-content" id="pills-tabContent">
                  <div
                    className="tab-pane fade show active"
                    id="pills-profile"
                    role="tabpanel"
                    aria-labelledby="pills-profile-tab"
                  >
                    {(loading1 || loadingPendingRequests) && (
                      <div className="text-center my-4">
                        <img
                          src={SkorDefaultLoader}
                          className="defaultSkorLoader"
                          alt="Example"
                        />
                      </div>
                    )}
                    <div className="edit-profile">
                      <div
                        className="tab-pane fade show active"
                        id="pills-home"
                        role="tabpanel"
                        aria-labelledby="pills-home-tab"
                      >
                        <div className="edit-profile">
                          <div className="Notifications-row new-notifications-row">
                            {!loadingPendingRequests &&
                              Array.isArray(filteredShortlisted) &&
                              !haveValue(searchText) &&
                              filteredShortlisted.length > 0 &&
                              filteredShortlisted?.map((landlord) => {
                                let property_linked = isPropertyLinked(
                                  landlord?.landlordid,
                                );
                                return (
                                  <>
                                    {
                                      <div
                                        className="Notifications-wapper opacity-wapper px-0 tt"
                                        key={landlord?.landlordid}
                                      >
                                        <div className="mobile-profile-info-box">
                                          <div className="userImageWrapper">
                                            <img src={UserIcon} alt="user" />
                                          </div>
                                          <div className="userDetails">
                                            <h4>
                                              {landlord?.landlord_fname}{" "}
                                              {landlord?.landlord_lname}
                                            </h4>
                                            {haveValue(property_linked) &&
                                              (property_linked?.propertyid
                                                ?.length > 0 ? (
                                                <p className="d-flex gap-2-vw locationText">
                                                  {property_linked?.propertyid
                                                    ?.map(
                                                      (props) =>
                                                        `${props?.street} - ${Number.isNaN(
                                                          parseInt(
                                                            props?.street_number,
                                                            10,
                                                          ),
                                                        )
                                                          ? "N/A"
                                                          : String(
                                                            parseInt(
                                                              props?.street_number,
                                                              10,
                                                            ),
                                                          )
                                                        }, ${props.floor_number
                                                        }-${props.flat_number}`,
                                                    )
                                                    .join(" / ")}
                                                </p>
                                              ) : (
                                                <p className="d-flex gap-2-vw invitation-sent-text">
                                                  {_t("noPropertyLinked")}
                                                </p>
                                              ))}
                                          </div>
                                        </div>

                                        <div className="share-it">
                                          <Link
                                            onClick={() => {
                                              onClose();
                                              setShowRemove(true);
                                              setRemoveLandlordId(
                                                landlord?.landlordid,
                                              );
                                            }}
                                            className="share-btn cancel-button"
                                          >
                                            {_t("unshare")}
                                          </Link>
                                        </div>
                                      </div>
                                    }
                                  </>
                                );
                              })}
                            {pendingShareRequests?.length > 0 &&
                              pendingShareRequests.map((request) => {
                                const requestId = request?._id;
                                const requestEmail =
                                  getPendingRequestEmail(request);
                                const createdAt = request?.created_at;
                                const isCanceling =
                                  cancelingRequestId === requestId;

                                return (
                                  <div
                                    className="Notifications-wapper opacity-wapper px-0 tt"
                                    key={requestId || requestEmail}
                                  >
                                    <div className="mobile-profile-info-box">
                                      <div className="userImageWrapper">
                                        <img src={UserIcon} alt="user" />
                                      </div>
                                      <div className="userDetails">
                                        <h4
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            flexWrap: "wrap",
                                          }}
                                          className="gap-2-vw"
                                        >
                                          <span className="email">
                                            {requestEmail}
                                          </span>
                                          <span className="pending-badge">
                                            {_t("pending")}
                                          </span>
                                        </h4>
                                        <p className="invitation-sent-text">
                                          {createdAt
                                            ? `${_t("invitationSent")} ${moment(
                                              createdAt,
                                            ).fromNow()}`
                                            : `${_t("invitationSent")}`}
                                        </p>
                                      </div>
                                    </div>

                                    <div className="share-it">
                                      <button
                                        type="button"
                                        onClick={() =>
                                          handleCancelShareRequest(requestId)
                                        }
                                        disabled={isCanceling}
                                        className="cancel-button share-btn"
                                      >
                                        {isCanceling
                                          ? `${_t("canceling")}...`
                                          : _t("cancel")}
                                      </button>
                                    </div>
                                  </div>
                                );
                              })}
                            {!loading1 &&
                              !loadingPendingRequests &&
                              pendingShareRequests?.length === 0 &&
                              filteredShortlisted.length === 0 && (
                                <p className="notSharedText text-center">
                                  {_t(
                                    "youHaventSharedYourProfileWithAnyLandlordOrAgentYet",
                                  )}
                                </p>
                              )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {showConsent && (
            <ConsentStepModal
              user={user}
              isPopup={true}
              onClose={() => setShowConsent(false)}
              loadSharedProfiles={loadSharedProfiles}
              loadUserTenant={loadUserTenant}
              selectedLandlord={selectedLandlord}
              shareProfileWithLandlord={() =>
                sendShareRequest(selectedLandlord?.email)
              }
            />
          )}
        </div>
      </div>
    )
  );
};

export default ShareProfileModal;
